// Chart Helper Functions
// This file contains utility functions for chart operations, formatting, and markers
import { state, COLD_COLOR_RGB, HOT_COLOR_RGB, PALETTE } from './chart-config.js';

// ============================================================================
// Timezone and Time Adjustment Functions
// ============================================================================

/**
 * Convert a display timestamp (from chart) back to its original UTC value
 */
function unadjustTimestampFromDisplay(displayTimestampMs) {
    const selectedTz = state.selectedTimezone || 'Etc/UTC';
    const adjustedMoment = moment.utc(displayTimestampMs);
    const parts = {
        year: adjustedMoment.year(),
        month: adjustedMoment.month(),
        date: adjustedMoment.date(),
        hour: adjustedMoment.hour(),
        minute: adjustedMoment.minute(),
        second: adjustedMoment.second(),
        millisecond: adjustedMoment.millisecond(),
    };
    return moment.tz(parts, selectedTz).valueOf();
}

/**
 * Adjust timestamp for display in the selected timezone
 */
function adjustTimestampForDisplay(timestamp) {
    const selectedTz = state.selectedTimezone || 'Etc/UTC';
    const offset = moment.tz(timestamp, selectedTz).utcOffset();
    return moment(timestamp).add(offset, 'minutes').valueOf();
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format timestamp as date and time
 */
function formatDateTime(timestamp) {
    // The timestamp is already adjusted for the timezone, so we treat it as UTC
    // for consistent formatting across the app.
    return moment.utc(timestamp).format('DD/MM HH:mm');
}

/**
 * Format duration between two timestamps
 */
function formatDuration(start, end) {
    const duration = moment.duration(end - start);
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    let parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 && days === 0) parts.push(`${minutes}m`);

    return parts.join(' ') || '<1m';
}

// ============================================================================
// Color Interpolation Functions
// ============================================================================



/**
 * Linearly interpolate between two RGB colors
 */
function interpolateColor(color1, color2, factor) {
    const result = color1.slice();
    for (let i = 0; i < 3; i++) {
        // Ensure factor is clamped between 0 and 1
        const clampedFactor = Math.max(0, Math.min(1, factor));
        result[i] = Math.round(color1[i] + clampedFactor * (color2[i] - color1[i]));
    }
    return `rgb(${result.join(', ')})`;
}

/**
 * Get color based on frequency ratio
 */
function getFrequencyColor(frequencyRatio) {
    return interpolateColor(COLD_COLOR_RGB, HOT_COLOR_RGB, frequencyRatio);
}

// ============================================================================
// Chart Marker Functions
// ============================================================================

/**
 * Render extreme markers on the chart
 */
function renderExtremeMarkers() {
    // If extreme markers are disabled, return empty array
    if (!state.extremeMarkersEnabled) {
        return [];
    }

    // Check if we're in split view mode - don't show markers
    if (state.currentViewMode === 'split') {
        return [];
    }

    // If no extreme timestamps available, return empty array
    if (!state.extremeTimestamps || state.extremeTimestamps.length === 0) {
        return [];
    }

    // Generate markers from extreme timestamps
    const markers = state.extremeTimestamps
        .map(timestamp => {
            // Convert timestamp to chart format, adjusting for the selected timezone
            const time = adjustTimestampForDisplay(new Date(timestamp.lastTimestamp).getTime()) / 1000;
            const isHigh = timestamp.isHigh;

            // Use simple color and shape based on high/low
            const color = isHigh ? PALETTE.markerHigh : PALETTE.markerLow; // green-500 or red-500
            const shape = isHigh ? 'arrowDown' : 'arrowUp'; // Arrow down for high, up for low
            const text = isHigh ? 'Max Difference' : 'Min Difference';

            return {
                time,
                position: isHigh ? 'aboveBar' : 'belowBar',
                color,
                shape,
                text,
                size: 2
            };
        });

    return markers;
}

/**
 * Update the rangeMarkers array based on current time ranges
 */
function updateRangeMarkers() {
    state.rangeMarkers = []; // Clear existing range markers

    // Positive Range Markers
    if (state.positivePctChangeTimeRange.startTime && state.positivePctChangeTimeRange.endTime) {
        state.rangeMarkers.push({
            time: adjustTimestampForDisplay(state.positivePctChangeTimeRange.startTime) / 1000,
            position: 'belowBar',
            color: PALETTE.rangePositive, // emerald-500
            shape: 'circle',
            text: 'Pos Start',
            size: 1.5
        });
        state.rangeMarkers.push({
            time: adjustTimestampForDisplay(state.positivePctChangeTimeRange.endTime) / 1000,
            position: 'belowBar',
            color: PALETTE.rangePositive, // emerald-500
            shape: 'circle',
            text: 'Pos End',
            size: 1.5
        });
    }

    // Negative Range Markers
    if (state.negativePctChangeTimeRange.startTime && state.negativePctChangeTimeRange.endTime) {
        state.rangeMarkers.push({
            time: adjustTimestampForDisplay(state.negativePctChangeTimeRange.startTime) / 1000,
            position: 'aboveBar',
            color: PALETTE.rangeNegative, // rose-500
            shape: 'circle',
            text: 'Neg Start',
            size: 1.5
        });
        state.rangeMarkers.push({
            time: adjustTimestampForDisplay(state.negativePctChangeTimeRange.endTime) / 1000,
            position: 'aboveBar',
            color: PALETTE.rangeNegative, // rose-500
            shape: 'circle',
            text: 'Neg End',
            size: 1.5
        });
    }
}

/**
 * Render all markers (extremes + ranges)
 */
function renderAllChartMarkers() {
    const extremeMarkers = renderExtremeMarkers(); // Get extreme markers (or empty array)
    const allMarkers = [...extremeMarkers, ...state.rangeMarkers]; // Combine with range markers
    if (state.dominanceDifferenceMarkers) {
        state.dominanceDifferenceMarkers.setMarkers(allMarkers);
    }
}

/**
 * Toggle extreme markers visibility
 */
function toggleExtremeMarkers() {
    state.extremeMarkersEnabled = !state.extremeMarkersEnabled;
    
    // Update UI button state
    const button = document.getElementById('toggleExtremesButton');
    if (button) {
        button.dataset.active = state.extremeMarkersEnabled.toString();
        button.querySelector('span').textContent = state.extremeMarkersEnabled ? 'Hide Extremes' : 'Show Extremes';
    }
    
    // Re-render all markers (extreme markers will be empty if disabled)
    renderAllChartMarkers();
}

// ============================================================================
// UI Helper Functions
// ============================================================================

/**
 * Update page title with dominance difference
 */
function updatePageTitle(dominanceDifference) {
    if (dominanceDifference !== null) {
        document.title = `Dominance Diff: ${dominanceDifference.toFixed(2)}% | Crypto Dominance`;
    } else {
        document.title = 'Crypto Dominance';
    }
}

/**
 * Update analysis button text with time range information
 */
function updateAnalysisButtonText(buttonId, startTime, endTime) {
    const button = document.getElementById(buttonId);
    if (button) {
        // 1. Format all the necessary parts
        const startText = formatDateTime(startTime);
        const endText = formatDateTime(endTime);
        const durationText = formatDuration(startTime, endTime); // Calculate duration

        // 2. Determine the prefix based on button ID
        const prefix = buttonId === 'positivePctChangeButton' ? 'Positive' : 'Negative';

        // 3. Construct the new, more detailed button text
        const buttonText = `${prefix} Pct Change (${startText} to ${endText}, ${durationText})`;

        // 4. Update the button's text content (assuming text is in a span)
        const textElement = button.querySelector('span');
        if (textElement) {
            textElement.textContent = buttonText;
        } else {
            // Fallback if no span is found (update button's direct text)
            button.textContent = buttonText;
            console.warn(`Button with ID "${buttonId}" does not have a <span> child for text content.`);
        }
    } else {
        console.warn(`Button with ID "${buttonId}" not found.`);
    }
}

/**
 * Get X coordinate from mouse or touch event
 */
function getXCoordinate(event) {
    if (event.clientX !== undefined) {
        return event.clientX; // Mouse event
    } else if (event.touches && event.touches.length > 0) {
        return event.touches[0].clientX; // Touch event
    }
    return null;
}

// ============================================================================
// ES6 Module Exports
// ============================================================================

export {
    unadjustTimestampFromDisplay,
    adjustTimestampForDisplay,
    formatDateTime,
    formatDuration,
    interpolateColor,
    getFrequencyColor,
    renderExtremeMarkers,
    updateRangeMarkers,
    renderAllChartMarkers,
    toggleExtremeMarkers,
    updatePageTitle,
    updateAnalysisButtonText,
    getXCoordinate,
};